type LobStaticKey = "TRAVEL" | "HEALTH" | "MOTOR" | "LIFE";

type LobRuntimeShape = {
  id?: number;
  lob_name?: string;
  display_name?: string;
};

const runtimeLobIds: Partial<Record<LobStaticKey, number>> = {};
const runtimeLobIdsByToken: Record<string, number> = {};

const LOB_NAME_TO_KEY: Record<string, LobStaticKey> = {
  travel: "TRAVEL",
  travel_insurance: "TRAVEL",
  health: "HEALTH",
  health_insurance: "HEALTH",
  motor: "MOTOR",
  car: "MOTOR",
  car_insurance: "MOTOR",
  life: "LIFE",
  life_insurance: "LIFE",
  term_life: "LIFE",
};

const normalizeLobToken = (value?: string) =>
  (value || "").trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");

const resolveLobToken = (lob: LobRuntimeShape): string => {
  const primary = normalizeLobToken(lob.lob_name);
  if (primary) return primary;
  return normalizeLobToken(lob.display_name);
};

const getStaticKeyForLob = (lob: LobRuntimeShape): LobStaticKey | undefined => {
  const byLobName = LOB_NAME_TO_KEY[normalizeLobToken(lob.lob_name)];
  if (byLobName) return byLobName;

  const byDisplayName =
    LOB_NAME_TO_KEY[normalizeLobToken(lob.display_name).replace(/_insurance/g, "")];
  return byDisplayName;
};

export const syncLobIdsFromApi = (lobs?: LobRuntimeShape[]) => {
  if (!Array.isArray(lobs)) return;

  // Keep this object fully API-driven by clearing previous runtime values.
  delete runtimeLobIds.TRAVEL;
  delete runtimeLobIds.HEALTH;
  delete runtimeLobIds.MOTOR;
  delete runtimeLobIds.LIFE;
  Object.keys(runtimeLobIdsByToken).forEach((key) => {
    delete runtimeLobIdsByToken[key];
  });

  lobs.forEach((lob) => {
    const token = resolveLobToken(lob);
    const key = getStaticKeyForLob(lob);

    const id = Number(lob.id);
    if (!Number.isFinite(id) || id <= 0) return;

    if (token) {
      runtimeLobIdsByToken[token] = id;
      runtimeLobIdsByToken[token.replace(/_insurance$/, "")] = id;
    }

    if (key) {
      runtimeLobIds[key] = id;
    }
  });
};

export const getLobIdByName = (token?: string): number | undefined => {
  const normalizedToken = normalizeLobToken(token);
  if (!normalizedToken) return undefined;

  const directMatch = runtimeLobIdsByToken[normalizedToken];
  if (directMatch) return directMatch;

  const mappedStaticKey = LOB_NAME_TO_KEY[normalizedToken];
  return mappedStaticKey ? runtimeLobIds[mappedStaticKey] : undefined;
};

export const getCoverageMasterEndpoint = (lob: string | number | undefined): string => {
  const lobId = typeof lob === "number" ? lob : getLobIdByName(lob);
  return `/master/coverage-master/${lobId}`;
};

export const Roles = {
  AGENT: "agent",
  MANAGER: "manager",
  RETAIL_CUSTOMER: "retail_customer",
  ADMIN: "admin",
  HEAD_OF_GROUP: "head_of_group",
  ACCOUNTS: "accounts",
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

export type LobId = number;

export const App_Url_New = {
  ApiEndPoint: {
    masterModules: "/auth/modules",
    masterModulesList: "/auth/modules/list",
    masterRoles: "/auth/roles",
    masterRolesList: "/auth/roles/list",
    masterActions: "/auth/actions",
    masterActionsList: "/auth/actions/list",
    motorGetQuote: "/motor/get-quotes",
    travelGetQuote: "/travel/get-quotes",
    healthGetQuote: "/health/get-quotes",
    lifeGetQuote: "/life/get-quotes",
    lifeIllustration: (insurerId: number, planKey: string) =>
      `/life/${insurerId}/illustrations/${planKey}`,
    motorMinMax: "/sum-insured/sum-insured",
    masterApi: "/master/coverage?is_active=true&order_by=asc",
    countryApi: "/master/countries",
    get addOnsApiMasterMotor() {
      return getCoverageMasterEndpoint("motor");
    },
    get addOnsApiMasterTravel() {
      return getCoverageMasterEndpoint("travel");
    },
    get addOnsApiMasterHealth() {
      return getCoverageMasterEndpoint("health");
    },
    get addOnsApiMasterLife() {
      return getCoverageMasterEndpoint("life");
    },
    healthQuoteMasterData: "/master/health-quote-config",
    quoteEmailSend: "/auth/client/get-quote-email",
    newQuoteEmailSend: "/users/get-quote-email",
    sendNotification: "/users/send-notification",
    sendUploadDocLink: "/auth/client/send-uploaddoc-link",
    sendPaymentLink: "/auth/client/send-payment-link",
    updateSession: "/users/update-session",
    createSession: "/users/create-session",
    mobileCountryCode: "/master/mobile-country-codes",
    getPolicy: "/policies/get-policy",
    getSession: "/users/get-session",
    getUserQuotes: "/users/get-quotes",
    getClientQuotes: "/users/get-client-quotes",
    downloadDocument: "/users/document/download",
    bindQuote: "/travel/bind-quotes",
    documentUpload: "/users/document/upload",
    travelGateway: "/travel/issue-policy",
    paymentGateway: "/users/make-payment",
    healthGateway: "/health/issue-policy",
    lifeGateway: "/life/issue-policy",
    motorGateway: "/motor/issue-policy",
    clientLeads: "/auth/client",
    clientAllocation: "/auth/client/allocation",
    userLogin: "/users/login",
    userData: "/auth/user/me",
    userList: "/users",
    createClient: "/auth/client/create-client",
    sendOtp: "/users/send-otp",
    verifyOtp: "/users/validate-otp",
    pipelineData: "/pipeline_data",
    quoteList: "/auth/quote/quotes",
    policyStatusList: "/policies/policy-status-list",
    teamMembers: "/auth/client/team-members",
    dashboardCount: "/auth/dashboard/dashboard",
    healthDashboard: "/auth/dashboard/health",
    apiMonitoring: "/api-monitoring",
    apiMonitoringStatus: "/api-monitoring/status",
    totalLeads: "/auth/dashboard/total-leads",
    quotesGenerated: "/auth/dashboard/quotes-generated-count",
    pipelineOverview: "/auth/dashboard/pipeline-overview-count",
    filters: "/master/lob-list/",
    policyIssued: "/auth/dashboard/policy-issued-count",
    policiesByInsurer: "/auth/dashboard/policies-by-insurer-count",
    conversionRate: "/auth/dashboard/conversion-rate-count",
    policyDetails: "/policies/policies-detail/",
    paymentGatewayList: "/auth/insurer/payment-gateway-list",
    insurerListing: "/auth/insurer/list",
    insurerDelete: "/auth/insurer/delete",
    insurerUpdate: "/auth/insurer/update",
    insurerAdd: "/auth/insurer/add",
    changeStatus: "/auth/insurer/change-status",
    providerServiceList: "/auth/provider-services",
    providerServiceTemplate: "/auth/provider-services/template",
    providerServiceTemplateAdd: "/auth/provider-services/template/add",
    providerServiceTemplateEdit: "/auth/provider-services/template/update",
    providerServiceTemplateDelete: "/auth/provider-services/template/delete",
    providerMasterList: "auth/auth-master/provider-list",
    providerServiceCreate: "/auth/provider-services/add",
    providerServiceUpdate: "/auth/provider-services/update",
    providerServiceDelete: "/auth/provider-services/delete",
    providerServiceMaster: "/auth/provider-services/service-master",
    logCount: "/auth/logs/count",
    currentInsurerType: "/motor/current-policy-types",
    currentInsurer: "/motor/current-insurers",
    noClaimCertificate: "/motor/no-claims-certificates",
    motorVehicles: "/motor/vehicles",
    commissionLedger: "/auth/commission-ledger",
    commissionStatus: "/auth/commission-ledger/filters/statuses",
    commissionInsurer: "/auth/commission-ledger/filters/insurers",
    commissionDateRange: "/auth/commission-ledger/date-ranges",
    locationDashboard: "/auth/organization/organization-hierarchy",
    ChannelTypes: "/auth/company/channels-types",
    upsertCompanyFields: "/auth/company/upsert-company-fields",
    channelTypeDocs: "/auth/company/channel-types-docs",
    upsertAgreementStatus: "/auth/company/upser-agrrement-status",
    EditChannel: "/auth/company/get-company-details/",
    ViewChannel: "/auth/company/get-company-details/",
    channelListing: "/auth/company/company/list",
    Profile: "/auth/user/profile",
    RetailProfile: "/auth/user/retail-profile",
    RoleAndPermissions: "/roles/permissions",
  },
  ApiOcrEndPoint: {
    ExtractDocument: "/users/extract-pdf",
  },
  defaultAccept: ".pdf,.jpg,.jpeg,.png",
  D2CPageUrl: {
    home: "/home",
    retailDashboard: "/retail-dashboard",
    insurance: {
      car: "/car-insurance",
      carGetQuote: "/car-get-quote",
      carPolicyResult: "/car-get-quote/policy",
      home: "/home-insurance",
      travel: "/travel-insurance",
      travelGetQuote: "/travel-get-quote",
      travelPolicyResult: "/travel-get-quote/policy",
      health: "/health-insurance",
      healthGetQuote: "/health-get-quote",
      healthPolicyResult: "/health-get-quote/policy",
      life: "/life-insurance",
      lifeGetQuote: "/life-get-quote",
      lifePolicyResult: "/life-get-quote/policy",
    },
  },
  CommonPageUrl: {
    login: "/login",
    actions: {
      getQuote: "/get-quote",
      retrievePolicy: "/retrieve-policy",
      allQuotes: "/get-quote/all-quotes",
      uploadDocuments: "/travel-get-quote?step=4",
    },
    services: {
      claims: "/claims",
      roadsideAssistance: "/roadside-assistance",
      complaints: "/complaints",
    },
    aboutUs: "/about-us",
    profile: "/profile",
  },
  B2CPageUrl: {
    dashboard: "/dashboard",
    clients: "/clients",
    quotes: "/quotes",
    addQuotes: "/quotes/add-quotes",
    editQuote: "/quotes/:quoteId/edit",
    clientFetchQuote: "/quotes/:quoteId/edit",
    pipeline: "/pipeline",
    policies: "/policies",
    calls: "/calls",
    settings: "/settings",

    // URL for Lovable I-Frame
    commissionDashboard: "/insurehub",
    commissionLedger: "/commission-ledger",
    invoices: "/invoices",
    reconciliation: "/reconciliation",
    variances: "/variances",
    tds: "/tds",
    reports: "/reports",
    irdaiReports: "/irdai",
    configuration: "/config",
    cases: "/cases",
    claims: "/claims",

    //
  },
  AdminPageUrl: {
    apiConfiguration: "/api-configuration",
    thirdPartyApi: "/api-monitoring",
    insurer: "/insurer",
    channels: "/channels",
    channelsAdd: "/channels/add",
    channelsEdit: "/channels/:id/edit",
    channelsView: "/channels/:id/channel-details",
    reviewQueue: "/review-queue",
    logs: "/logs",
  },
  DocumentIcons: {
    pdf: "📄",
    idCard: "🪪",
    passport: "📘",
  },
  PopUpStatus: {
    SHOW: "show",
    HIDE: "hide",
  },
  PopupKeys: {
    AUTH_RETAIL_LOGIN: "AUTH_RETAIL_LOGIN",
    AUTH_CAR_QUOTE_RESUME: "AUTH_CAR_QUOTE_RESUME",
    AUTH_TRAVEL_QUOTE_RESUME: "AUTH_TRAVEL_QUOTE_RESUME",
    LOGOUT_CONFIRM: "LOGOUT_CONFIRM",
    AUTH_HEALTH_QUOTE_RESUME: "AUTH_HEALTH_QUOTE_RESUME",
    AUTH_LIFE_QUOTE_RESUME: "AUTH_LIFE_QUOTE_RESUME",
    COMPARE_QUOTES: "COMPARE_QUOTES",
    QUOTE_DETAILS: "QUOTE_DETAILS",
    TRAVEL_QUOTE_DETAIL: "TRAVEL_QUOTE_DETAIL",
    CAR_QUOTE_DETAIL: "CAR_QUOTE_DETAIL",
    OPEN_CHAT_BOT: "OPEN_CHAT_BOT",
  },
  OcrApiFields: {
    HEALTH: {
      insurance_type: "Health",
    },
    EMIRATES_ID: {
      insurance_type: "Emirates_Id",
    },
    DRIVING_LICENSE_FRONT: {
      insurance_type: "Driving_License_Front",
    },
    DRIVING_LICENSE_BACK: {
      insurance_type: "Driving_License_Back",
    },
    MOTOR: {
      insurance_type: "Motor",
    },
    PASSPORT: {
      insurance_type: "Passport_Number",
    },
    PASSPORT_ADDRESS: {
      insurance_type: "Passport_Address",
    },
    Life_Quotes: "Life_Quote",
  },
};

export const EMAIL_TEMPLATE_KEYS = {
  GET_QUOTE_EMAIL: "GET_QUOTE_EMAIL",
  UPLOAD_DOC_LINK: "UPLOAD_DOC_LINK",
  PAYMENT_LINK: "PAYMENT_LINK",
} as const;

export const pipelineMapping = {
  travel: {
    step_1_pre_request: "Quote Initiated",
    step_2_pre_request: "Quote Initiated",
    quote_request: "Quotes Given",
    step_3_pre_request: "Quotes Given",
    step_4_pre_request: "Quotes Given",
    selected_quote: "Quotes selected",
    step_5_post_request: "Document Upload",
    step_6_post_request: "Additional Info Taken",
    step_7_post_request: "Payment Failed",
    step_8_post_request: "Policy Issued",
  },
} as const;

export const getInitialPipelineColumns = (options?: { hideAdditionalInfoTaken?: boolean }) => ({
  quote_initiated: {
    id: "col-1",
    title: "Quote Initiated",
    count: 0,
    totalValue: 0,
    colorCode: "bg-theme-info-bright",
    leads: [] as any[],
  },
  quote_request: {
    id: "col-2",
    title: pipelineMapping.travel.quote_request,
    count: 0,
    totalValue: 0,
    colorCode: "bg-theme-success-bright",
    leads: [] as any[],
  },
  ...(!options?.hideAdditionalInfoTaken && {
    step_6_post_request: {
      id: "col-3",
      title: pipelineMapping.travel.step_6_post_request,
      count: 0,
      totalValue: 0,
      colorCode: "bg-theme-warning-bright",
      leads: [] as any[],
    },
  }),
  step_7_post_request: {
    id: "col-4",
    title: pipelineMapping.travel.step_7_post_request,
    count: 0,
    totalValue: 0,
    colorCode: "bg-theme-danger-bright",
    leads: [] as any[],
  },
});

export const methodOptions = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
  { label: "PUT", value: "PUT" },
  { label: "DELETE", value: "DELETE" },
  { label: "PATCH", value: "PATCH" },
];

export const METHOD_CONFIG: Record<string, string> = {
  POST: "bg-blue-100 text-blue-700",
  GET: "bg-green-100 text-green-700",
  PUT: "bg-yellow-100 text-yellow-700",
  DELETE: "bg-red-100 text-red-700",
  PATCH: "bg-purple-100 text-purple-700",
};

export const EXECUTION_TYPE_CONFIG: Record<string, string> = {
  SINGLE: "bg-emerald-100 text-emerald-700",
  MULTI: "bg-violet-100 text-violet-700",
  SEQUENTIAL: "bg-amber-100 text-amber-700",
  PARALLEL: "bg-cyan-100 text-cyan-700",
};

export const LOB_CONFIG: Record<string, string> = {
  motor: "bg-orange-100 text-orange-700",
  travel: "bg-cyan-100 text-cyan-700",
  health: "bg-pink-100 text-pink-700",
  life: "bg-indigo-100 text-indigo-700",
  home: "bg-lime-100 text-lime-700",
};

export const getBadgeClass = (value?: string, config?: Record<string, string>): string => {
  if (!value) return "bg-gray-100 text-gray-600";
  return config?.[value] || config?.[value.toLowerCase()] || "bg-slate-100 text-slate-700";
};
